import React, { useState } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Download } from 'lucide-react';
import api from '../../services/api';

const ExcelUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          selectedFile.type === 'application/vnd.ms-excel') {
        setFile(selectedFile);
        setResult(null);
      } else {
        alert('Lütfen geçerli bir Excel dosyası seçin (.xlsx veya .xls)');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setResult({
        success: false,
        message: 'Lütfen önce bir Excel dosyası seçin (.xlsx veya .xls formatında)'
      });
      return;
    }

    const formData = new FormData();
    formData.append('excelFile', file);

    setUploading(true);
    setProgress(0);

    try {
      const response = await api.post('/excel/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgress(percentCompleted);
        },
      });

      setResult({
        success: true,
        message: response.data.message,
        data: response.data.data
      });
      setFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('excelFile');
      if (fileInput) fileInput.value = '';

    } catch (error) {
      console.error('Excel yükleme hatası:', error);
      setResult({
        success: false,
        message: error.response?.data?.message || 'Excel dosyası yüklenirken bir hata oluştu'
      });
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await api.get('/excel/download-template', {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'urun_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Template indirildi');
    } catch (error) {
      console.error('❌ Template indirme hatası:', error);
      alert('Template indirilemedi. Lütfen tekrar deneyin.');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Excel ile Ürün Yükleme</h1>
        <p className="text-gray-600">Excel dosyası ile toplu ürün ekleyebilirsiniz</p>
      </div>

      {/* Template İndirme */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-center mb-3">
          <Download className="h-5 w-5 text-blue-600 mr-2" />
          <h3 className="text-lg font-semibold text-blue-900">Excel Template</h3>
        </div>
        <p className="text-blue-700 mb-3">
          Doğru formatta Excel dosyası oluşturmak için template dosyasını indirin.
        </p>
        <button
          onClick={downloadTemplate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          📥 Template İndir
        </button>
      </div>

      {/* Excel Yükleme Formu */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Excel Dosyası Yükle</h3>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <div className="mb-4">
              <label htmlFor="excelFile" className="cursor-pointer block">
                <span className="text-lg font-medium text-gray-700 hover:text-blue-600 transition-colors">
                  {file ? '✅ Dosya Seçildi' : '📁 Excel dosyasını seçin'}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  .xlsx veya .xls formatında
                </p>
              </label>
              <input
                id="excelFile"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
            
            {file && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <div className="flex items-center">
                  <FileText className="h-5 w-5 text-green-600 mr-2" />
                  <span className="text-green-800 font-medium">{file.name}</span>
                </div>
                <p className="text-sm text-green-600 mt-1">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            )}
          </div>

          {/* Progress Bar */}
          {uploading && (
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Yükleniyor...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
              !file || uploading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {uploading ? 'Yükleniyor...' : !file ? 'Önce Excel Dosyası Seçin' : 'Ürünleri Yükle'}
          </button>
        </div>

        {/* Sonuç Mesajı */}
        {result && (
          <div className={`border rounded-lg p-4 ${
            result.success 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center mb-2">
              {result.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
              )}
              <span className={`font-medium ${
                result.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {result.success ? 'Başarılı!' : 'Hata!'}
              </span>
            </div>
            <p className={`${
              result.success ? 'text-green-700' : 'text-red-700'
            }`}>
              {result.message}
            </p>
            
            {result.success && result.data && (
              <div className="mt-3 text-sm text-green-600">
                <p>✅ Toplam ürün: {result.data.totalProducts}</p>
                <p>✅ Başarıyla eklenen: {result.data.successCount}</p>
                <p>⚠️ Hatalı ürün: {result.data.errorCount}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Kullanım Talimatları */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Kullanım Talimatları</h3>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="flex items-start">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
            <p>Template dosyasını indirin ve Excel ile açın</p>
          </div>
          <div className="flex items-start">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
            <p>Ürün bilgilerini template formatında doldurun</p>
          </div>
          <div className="flex items-start">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
            <p>Görsel URL'lerini doğru formatta ekleyin</p>
          </div>
          <div className="flex items-start">
            <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
            <p>Dosyayı kaydedin ve buraya yükleyin</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelUpload;
